import React, { Component } from 'react';
import i18n from 'i18n-react';
import Breadcrumbs from 'react-router-dynamic-breadcrumbs';
import posed, { PoseGroup } from 'react-pose';

import 'assets/styles/sweetalert.css';
import { legacyRemCss } from 'helpers/legacyRem';

import { Redirect, withRouter, Link } from 'react-router-dom';

import Progress from 'components/Progress';
import SweetAlert from 'components/SweetAlert';
import Toast from 'components/Toast';
import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import ProgressHandler from 'config/ProgressHandler';
import StorageHandler from 'config/StorageHandler';

import { Tooltip } from 'react-tooltip';

import TrainingContent, { LinkButton, NavigationButton } from 'components/TrainingContent';

import { storageKeys, buttonRules, fastTrackId } from 'config/settings.json';
import { fastTrackData } from 'helpers/FastTrack/fastTrackData';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

const AnimationContainer = posed.section({
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 200,
    },
  },
  enter: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 200,
    },
  },
});

const incrementBreadcrumbPage = (crumbPageId) => {
  return +crumbPageId + 1;
};

class TrainingContentView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      appId: -1,
      lang: '',
      badge: '',
      buttonContentCompleted: ['retain', 'fasttrack'].includes(props.type),
      timerCompleted: ['retain', 'fasttrack'].includes(props.type),
      buttons: [],
      buttonsCompleted: 0,
      completedBefore: null,
      contentCompleted: props.type === 'retain',
      crumbRoutes: {},
      errors: 0,
      onCorrect: false,
      pages: {},
      ready: false,
      user: '',
      sweetAlertTitle: null,
      pageNotCompleted: false,
      chapterTitle: '',
    };

    this.toastFired = false;
  }

  componentDidMount() {
    const d = new Date(Date.now());
    const leading = (val) => {
      return `0${val}`.slice(-2);
    };

    // Clear visitedLinks on page refresh
    StorageHandler.remove('visitedLinks');

    this.init();

    StorageHandler.load(storageKeys.user).then((user) => {
      if (user && user.training_started === null) {
        const obj = user;
        const date = `${d.getFullYear()}-${leading(d.getMonth() + 1)}-${leading(d.getDate())}`;
        DataHandler.matchAppId().then((appId) => {
          APIHandler.updateUser(appId, obj.email, [{ param: 'training_started', val: date }]).then((res) => {
            if (res) {
              obj.training_started = date;
              StorageHandler.save(storageKeys.user, obj);
            }
          });
        });
      }
    });
  }

  componentDidUpdate() {
    const { type, match } = this.props;
    const { pageId, chapterId } = match.params;
    const { completedBefore } = this.state;
    if (this.progress !== undefined && completedBefore === null && type !== 'fasttrack') {
      this.checkCompletion(pageId, chapterId);
    }
  }

  setButtons(page) {
    const buttons = [];
    let index = 1;
    let mandatoryAmount = 0;

    if (page !== undefined) {
      // Lock page for 15 seconds (mandatory reading time)
      this.setState({ timerCompleted: true });
      if (page.title && page.pageType !== 'Infographic' && page.pageType !== 'Photo') {
        this.setState({ timerCompleted: false });
        setTimeout(() => {
          this.setState({
            timerCompleted: true,
          });
        }, 5000);
      }

      Object.keys(page).forEach(() => {
        if (buttonRules[page[`linkType${index}`]]) {
          mandatoryAmount += 1;
        }
        index += 1;
      });

      index = 1;
      Object.keys(page).forEach((link) => {
        const mandatory = buttonRules[page[`linkType${index}`]];
        const links = [page[`link${index}`], page[`linkType${index}`]].every((val) => {
          return val !== undefined && val.length > 0;
        });
        const linksValid = page.question === undefined && links;
        if (link === `link${index}` && linksValid) {
          const trainingCompleted = !!this.state.completedBefore || !!this.state.buttonContentCompleted;
          buttons.push(
            <LinkButton
              key={page[`linkType${index}`]}
              link={page[`linkType${index}`] === 'slideshow' ? page[`link_slideshow_${index}`] : page[`link${index}`]}
              type={page[`linkType${index}`]}
              htmlText={page[`linkTextHtml${index}`]}
              shareTitle={page[`linkTitle${index}`] || ''}
              mandatory={mandatory}
              index={index}
              trainingCompleted={trainingCompleted}
              completed={() => {
                const { buttonsCompleted } = this.state;
                const newButtonAmount = buttonsCompleted + 1;
                this.setState({ buttonsCompleted: newButtonAmount });
                if (newButtonAmount === mandatoryAmount) {
                  this.setState({ buttonContentCompleted: true });
                }
              }}
            />,
          );
          index += 1;
        }
      });
      this.setState({ buttons });
    }

    if (mandatoryAmount === 0) {
      this.setState({ buttonContentCompleted: true });
    }
  }

  checkCompletion(pageIdOld, chapterId) {
    const { pageId } = this.props.match.params;
    const { chapters } = this.progress.progress.data[0];
    const { progress } = chapters.filter((chapter) => {
      return chapter.chapter === chapterId;
    })[0];
    const completedBefore = progress > +pageId;
    this.setState({ completedBefore }, () => {
      // Call setButtons again with the latest completedBefore
      const { pages } = this.state;
      if (pages && pages[+pageId]) {
        this.setButtons(pages[+pageId]);
      }
    });
  }

  navigationButtonCopy(pageId, chapterId) {
    const { pages } = this.state;
    if (this.progress) {
      const { chapters } = this.progress.progress.data[0];
      const lastChapter = chapters.slice(-1)[0].chapter;

      if (+pageId === pages.length - 1) {
        // Return complete course completed copy for final chapter instead of chapter complete copy
        if (lastChapter === chapterId) {
          return i18n.translate('general.courseComplete');
        }

        return i18n.translate('general.chapterComplete');
      }
    } else if (+pageId === pages.length - 1) {
      return i18n.translate('general.courseComplete');
    }
    return i18n.translate('general.next');
  }

  initPages(lang = this.state.lang) {
    const { type, match } = this.props;
    const { chapterId, pageId } = match.params;
    return new Promise((resolve) => {
      DataHandler.matchAppId().then((appId) => {
        this.setState({ appId });

        this.page = +pageId + 1;

        if (type === 'fasttrack') {
          APIHandler.getAppQuestions(fastTrackId).then((pages) => {
            fastTrackData('totalQuestions', pages.length);
            this.setState({ pages }, resolve());
          });
        } else {
          let fetchedPages = [];
          APIHandler.getTrainingPages(chapterId, lang).then((pages) => {
            if (type === 'retain') {
              pages.forEach((page) => {
                if (page.type === undefined && page.pageType !== 'Final-Page') {
                  fetchedPages.push(page);
                }
              });
            } else fetchedPages = pages;
            this.setButtons(fetchedPages[+pageId]);
            this.setState({ pages: fetchedPages }, resolve());
          });
        }
      });
    });
  }

  initProgress() {
    const { chapterId, pageId } = this.props.match.params;
    const { user } = this.state;
    return new Promise((resolve) => {
      DataHandler.matchAppId().then((appId) => {
        DataHandler.checkKey(storageKeys.progress, APIHandler.getUserProgress(appId, user)).then((progress) => {
          resolve({
            progress,
            chapter: chapterId,
            page: pageId,
            pageCount: this.state.pages.length,
          });
        });
      });
    });
  }

  updateProgressOnCorrect() {
    const { pageId, chapterId } = this.props.match.params;
    const { user } = this.state;

    DataHandler.matchAppId().then((appId) => {
      DataHandler.checkKey(storageKeys.progress, APIHandler.getUserProgress(appId, user)).then((progress) => {
        this.progress = new ProgressHandler({
          chapter: chapterId,
          email: user,
          page: +pageId + 1,
          pageCount: this.state.pages.length,
          progress,
        });
        this.progress.update();
      });
    });
  }

  nextPage() {
    const { history, type } = this.props;
    const { pageId, chapterId } = this.props.match.params;
    const { user } = this.state;
    window.scrollTo(0, 0);

    // Clear visitedLinks on next page
    StorageHandler.remove('visitedLinks');

    const { contentCompleted, buttonContentCompleted, completedBefore, timerCompleted } = this.state;
    const contentIncomplete = !contentCompleted || !buttonContentCompleted || !timerCompleted;

    if (chapterId === '2090' && pageId === '26') {
      // Open fast track if last page + 2090
      this.setState({ contentCompleted: true, completedBefore: null });
      window.location.replace('/main/fasttrack/chapter/0/0');
      return false;
    }

    this.setButtons(this.state.pages[+pageId + 1]);

    if (['training'].includes(type)) {
      this.setState({
        contentCompleted: false,
        completedBefore: null,
        errors: 0,
      });
    }

    if (!['fasttrack'].includes(type)) {
      if ((!completedBefore && contentIncomplete) || (contentIncomplete && !completedBefore)) {
        this.setState({
          pageNotCompleted: true,
          sweetAlertTitle: i18n.translate('trainingScreens.viewAll'),
          errors: 2,
        });
        return false;
      }
    }

    DataHandler.matchAppId().then((appId) => {
      DataHandler.checkKey(storageKeys.progress, APIHandler.getUserProgress(appId, user)).then((progress) => {
        if (+pageId + 1 === this.state.pages.length - 1 && ['training'].includes(type)) {
          // Make chapter completed api call page before last page
          APIHandler.getChapters(appId).then((response) => {
            const chapter = response.app.filter((o) => {
              return Object.values(o).includes(chapterId);
            });
            APIHandler.setChapterCompleted(appId, user, chapterId, chapter[0].chapterNumber).then(() => {
              this.page = +pageId + 1;
            });
          });
        } else if (+pageId + 1 < this.state.pages.length && ['training', 'fasttrack'].includes(type)) {
          this.page = +pageId + 1;
        } else if (type === 'training') {
          return this.progress.update().then(() => {
            return history.push('/main/training');
          });
        }
        if (type === 'training') {
          this.progress = new ProgressHandler({
            chapter: chapterId,
            email: user,
            page: this.page,
            pageCount: this.state.pages.length,
            progress,
          });

          try {
            return this.progress.update().then(() => {
              const target = `${this.props.redirPath}${chapterId || 0}/${this.progress.page}`;
              this.setState({ ready: true });
              if (this.props.match.url !== target) {
                history.push(target);
              }
            });
          } catch (error) {
            console.error(error);
          }
        }
        if (['retain', 'fasttrack'].includes(type)) {
          if (+pageId + 1 < this.state.pages.length) {
            this.page = +pageId + 2;
            fastTrackData('answered', (+fastTrackData('answered') || 0) + 1);
            history.push(`${this.props.redirPath}${chapterId || 0}/${+pageId + 1}`);
          } else {
            if (type === 'fasttrack') {
              fastTrackData('finished', true);
              fastTrackData('answered', (+fastTrackData('answered') || 0) + 1);
            }
            history.push('/main/training');
          }
        }
        return false;
      });
    });
  }

  prevPage() {
    const { history, type } = this.props;
    const { pageId, chapterId } = this.props.match.params;
    const { user } = this.state;
    const prevPageNum = +pageId - 1;
    window.scrollTo(0, 0);

    // Clear visitedLinks on previous page
    StorageHandler.remove('visitedLinks');

    this.setButtons(this.state.pages[prevPageNum]);
    this.setState({ completedBefore: null });

    DataHandler.matchAppId().then((appId) => {
      DataHandler.checkKey(storageKeys.progress, APIHandler.getUserProgress(appId, user)).then((progress) => {
        if (+pageId > 0 && type === 'training') {
          this.progress = new ProgressHandler({
            chapter: chapterId,
            email: user,
            page: prevPageNum,
            pageCount: this.state.pages.length,
            progress,
          });

          try {
            return this.progress.update().then(() => {
              const target = `${this.props.redirPath}${chapterId || 0}/${this.progress.page}`;
              this.setState({ ready: true });
              if (this.props.match.url !== target) {
                history.push(target);
              }
            });
          } catch (error) {
            console.error(error);
          }
        } else {
          this.setState({ ready: true });
          history.push(`/main/${type}`);
        }

        if (['retain', 'fasttrack'].includes(type)) {
          if (+pageId > 0) {
            history.push(`${this.props.redirPath}${chapterId || 0}/${prevPageNum}`);
          } else history.push('/main/retain');
        }

        return false;
      });
    });
  }

  async init() {
    const { history, type } = this.props;
    const { chapterId, pageId } = this.props.match.params;

    const { langFile: language, code } = await StorageHandler.load(storageKeys.lang);
    this.setState({ lang: code });

    await this.initPages(code);

    const user = (await StorageHandler.load(storageKeys.user)) || {};
    if (user) {
      const { email } = user;
      this.setState({ user: email });
    }

    const mappedChapters = {};
    const appId = await DataHandler.matchAppId();

    const { chapters: infoChapters } = await APIHandler.getChaptersAndProgress(appId, user.email, code);
    let AllChapters = [];
    if (
      infoChapters &&
      infoChapters.uncategorized &&
      infoChapters.uncategorized[0] &&
      infoChapters.uncategorized[0].chapters
    ) {
      AllChapters = infoChapters.uncategorized[0].chapters;
    }

    AllChapters.forEach(({ id, translations, badge }) => {
      if (language !== 'en' && id === Number(chapterId)) {
        const translation =
          translations &&
          translations.filter(({ locale }) => {
            return locale.includes(language.toUpperCase());
          });
        if (translation && translation.length > 0 && translation[0].badge) {
          this.setState({ badge: translation[0].badge });
        }
      } else if (language === 'en' && id === Number(chapterId)) {
        this.setState({ badge });
      }
    });

    const { app: chapters = [] } =
      (await DataHandler.checkKey(storageKeys.chapters, APIHandler.getChapters(appId))) || {};

    if (pageId === 'null') {
      history.push(`${this.props.redirPath}${chapterId}/0`);
    }
    chapters.forEach(({ chapterID, chapterTitle }) => {
      mappedChapters[chapterID] = chapterTitle;
    });

    this.setState({
      chapterTitle: mappedChapters[String(chapterId)] || mappedChapters[Number(chapterId)] || '',
    });

    this.setState({
      crumbRoutes: {
        '/main': null,
        [`/main/${type}`]: null,
        [`/main/${type}/chapter`]: null,
        [`/main/${type}/chapter/:crumbChapterId`]: null,
        [`/main/${type}/chapter/:crumbChapterId/:crumbPageId`]: (url, match) => {
          return `${mappedChapters[match[':crumbChapterId']]} - ${i18n.translate(
            'breadCrumbs.page',
          )} ${incrementBreadcrumbPage(match[':crumbPageId'])}`;
        },
      },
    });

    if (type !== 'fasttrack') {
      const progress = await this.initProgress();
      this.progress = new ProgressHandler(progress);

      this.setState({ ready: true });
    } else this.setState({ ready: true });
  }

  renderContent() {
    const { pageId } = this.props.match.params;
    const { type, history } = this.props;
    const { appId, badge, pages, sweetAlertTitle, errors, contentCompleted, onCorrect, completedBefore, chapterTitle } =
      this.state;
    const totalErrors = +fastTrackData('errors') || 0;
    if (type === 'fasttrack' && totalErrors === -1) {
      history.push('/main/training');
    }

    const { incorrectAnswerText, correctAnswerText, question } = pages?.[pageId] ?? {};

    const missingAnswerResponseText = incorrectAnswerText === undefined || correctAnswerText === undefined;

    if (question !== undefined && missingAnswerResponseText) {
      return null;
    }

    let onError = errors >= 1;
    return (
      <TrainingContent
        key={pageId}
        type={type}
        appId={appId}
        badge={badge}
        chapterTitle={chapterTitle}
        params={pages[pageId]}
        pageId={pageId}
        LinkButtons={this.state.buttons}
        renderNonce={`${errors}-${contentCompleted}-${onCorrect}`}
        contentComplete={() => {
          this.setState({
            contentCompleted: true,
            errors: 0,
            // completedBefore: true,
          });
          if (pages[pageId].type !== undefined) {
            // Update progress when content is completed (for non-question pages)
            this.updateProgressOnCorrect();
          }
        }}
        completedBefore={completedBefore}
        correct={(increment) => {
          if (increment) {
            // Reset the flag when starting a new correct answer
            this.toastFired = false;
            this.setState({ onCorrect: true, completedBefore: true });
            // Update progress immediately when question is answered correctly
            this.updateProgressOnCorrect();
            // Reset trigger after a short delay to prevent multiple toasts
            setTimeout(() => {
              this.setState({ onCorrect: false });
            }, 100);
            if (type === 'fasttrack') {
              const correctAnswers = fastTrackData('correctAnswers') || [];
              const page = pages?.[pageId]; // safe lookup

              if (page && typeof page === 'object' && 'UPID' in page) {
                fastTrackData('correctAnswers', [...correctAnswers, page.UPID]);
              }
            }
          }
          // Only trigger toast if onCorrect is true and we haven't fired yet
          const shouldTrigger = onCorrect && !this.toastFired;
          if (shouldTrigger) {
            this.toastFired = true;
          }
          return (
            <Toast
              key={`toast-${pageId}-${shouldTrigger}`}
              trigger={shouldTrigger}
              onOpen={() => {
                this.setState({ onCorrect: false });
                this.toastFired = false;
              }}
              type="success"
              position="top-center"
              content={correctAnswerText}
            />
          );
        }}
        error={(increment) => {
          const { errors: e } = this.state;
          if (increment) {
            this.setState({ errors: e + 1 });
            if (type === 'fasttrack') {
              fastTrackData('errors', totalErrors + 1);
            }
          }
          if (completedBefore) {
            onError = false;
          }
          let titleCopy =
            sweetAlertTitle ||
            i18n.translate(
              (type === 'fasttrack' && (onError ? incorrectAnswerText : correctAnswerText)) ||
                `trainingScreens.${type === 'fasttrack' ? 'incorrect' : 'trouble'}`,
              {
                errors: 3 - totalErrors,
                mistakes: i18n.translate(`trainingScreens.mistake${3 - totalErrors > 1 ? 's' : ''}`),
              },
            );
          let iconPopup = '';
          let closeCopy = i18n.translate('trainingScreens.skip');
          if (onError && type === 'fasttrack') {
            iconPopup = 'error';
            closeCopy = i18n.translate('trainingScreens.next');
          } else if (type === 'training' && errors === 1) {
            iconPopup = 'error';
            closeCopy = i18n.translate('general.close');
            titleCopy = incorrectAnswerText;
          } else if (type === 'training' && errors >= 2) {
            iconPopup = '';
            closeCopy = i18n.translate('trainingScreens.skip');
          } else if (type === 'fasttrack') {
            iconPopup = 'success';
            closeCopy = i18n.translate('trainingScreens.next');
          }

          return (
            <SweetAlert
              key={`alert-${pageId}-${errors}-${contentCompleted}-${onError}`}
              trigger={(type === 'fasttrack' && contentCompleted) || onError}
              {...{
                onClose: ({ dismiss, value }) => {
                  onError = false;
                  if (value === true) {
                    if (type !== 'fasttrack' && errors !== 1) {
                      this.setState({ errors: 0 }, () => {
                        return this.prevPage();
                      });
                    } else if (type === 'fasttrack') {
                      this.setState({ errors: 0 }, () => {
                        return this.nextPage();
                      });
                    }
                  }
                  if (dismiss && onError) this.setState({ errors: 0 });
                },
                onOpen: () => {
                  if (!onError) {
                    const correctAnswers = fastTrackData('correctAnswers') || [];
                    fastTrackData('correctAnswers', [...correctAnswers, pages[pageId] && pages[pageId].UPID]);
                  }
                },
                icon: iconPopup,
                allowOutsideClick: false,
                confirmButtonText: closeCopy,
                title: titleCopy,
              }}
            />
          );
        }}
      />
    );
  }

  render() {
    const { type, match } = this.props;
    const { pageId, chapterId } = match.params;
    const {
      pages,
      contentCompleted,
      completedBefore,
      buttonContentCompleted,
      ready,
      timerCompleted,
      pageNotCompleted,
    } = this.state;

    const contentIncomplete = !contentCompleted || !buttonContentCompleted || !timerCompleted;

    return (
      <React.Fragment>
        {ready && (type === 'fasttrack' || this.progress.pageIsAccessible().valid) && (
          <div className="progress__container" key="progress__container">
            <Progress id="training_progress" value={Math.round((+pageId / (pages.length - 1)) * 100)} />
          </div>
        )}
        <PoseGroup flipMove={false}>
          {ready && (
            <AnimationContainer className="training" key={pageId}>
              <div className="trainingBackBtn">
                <Link to="/main/training" className="backBtn" aria-label={i18n.translate('general.back')}>
                  <FontAwesomeIcon icon={['fas', 'times']} />
                </Link>
              </div>
              <style>{`#openWidget { display: block; bottom: ${legacyRemCss(4)}; }`}</style>
              <section className="trainingContent" key="trainingContent">
                {ready && (type === 'fasttrack' || this.progress.pageIsAccessible().valid) && (
                  <div>
                    <Breadcrumbs
                      mappedRoutes={this.state.crumbRoutes}
                      WrapperComponent={(props) => {
                        return <ul className="breadcrumb__List">{props.children}</ul>;
                      }}
                      LinkComponent={(props) => {
                        return (
                          <li className="breadcrumb__item">
                            {props.children}
                            <span className="breadcrumb__separator">&#62;</span>
                          </li>
                        );
                      }}
                      ActiveLinkComponent={(props) => {
                        return <li className="breadcrumb__item--active">{props.children}</li>;
                      }}
                    />
                    <style>{`#openWidget { display: block; bottom: ${legacyRemCss(4)}; }`}</style>
                    <div id="parent" className="trainingContent__content">
                      {this.renderContent()}
                    </div>
                    <div className="trainingContent__buttons">
                      {type !== 'fasttrack' && (
                        <NavigationButton
                          button={{
                            navigate: i18n.translate(`general.${+pageId === 0 ? 'overview' : 'previous'}`),
                            disabled: type === 'fasttrack',
                            onClick: () => {
                              this.prevPage();
                            },
                            type: 'previous',
                          }}
                        />
                      )}
                      {+pageId === 0 && type === 'fasttrack' && <span />}
                      <div className="trainingContent__pageNumber">
                        <p>
                          {+pageId + 1}
                          {' / '}
                          {pages.length}
                        </p>
                      </div>
                      {((+pageId === 0 && type === 'fasttrack') || type !== 'fasttrack') && (
                        <React.Fragment>
                          <div
                            id="tooltipAnchorWrapper"
                            data-tooltip-hidden={
                              !((!completedBefore && contentIncomplete) || (contentIncomplete && !completedBefore))
                            }
                          >
                            <NavigationButton
                              button={{
                                className: clsx('trainingContentView__nextButton', {
                                  disabled:
                                    (!completedBefore && contentIncomplete) || (contentIncomplete && !completedBefore),
                                }),
                                navigate: this.navigationButtonCopy(pageId, chapterId),
                                onClick: () => {
                                  this.nextPage();
                                },
                                type: 'next',
                                disabled:
                                  import.meta.env.PROD &&
                                  ((!completedBefore && contentIncomplete) || (contentIncomplete && !completedBefore)),
                                // id: `nextButton${
                                //   (!completedBefore && contentIncomplete) || (contentIncomplete && !completedBefore)
                                // }`,
                                // id: `nextButton`,
                              }}
                            />
                          </div>
                          <Tooltip
                            anchorSelect="#tooltipAnchorWrapper"
                            className="trainingContentView__tooltip"
                            place="top"
                          >
                            <div>
                              <h3 className="tooltip__title">{i18n.translate('trainingScreens.viewAllTitle')}</h3>
                              <p className="tooltip__text">{i18n.translate('trainingScreens.viewAll')}</p>
                            </div>
                          </Tooltip>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                )}
                {this.progress !== undefined &&
                  type !== 'fasttrack' &&
                  ready &&
                  (() => {
                    const access = this.progress.pageIsAccessible();

                    if (!this.progress.verifyPage() || !access.valid) {
                      const safeTarget = `${this.props.redirPath}${chapterId}/${access.page ?? 0}`;
                      return <Redirect push to={safeTarget} />;
                    }

                    return null;
                  })()}
              </section>
            </AnimationContainer>
          )}
        </PoseGroup>
        <SweetAlert
          trigger={pageNotCompleted}
          {...{
            onClose: () => {
              // Only bypass the automatic unlock in development mode.
              if (import.meta.env.DEV) {
                console.info('development mode: bypassing incomplete page block && marking page as completed');
                this.setState({
                  pageNotCompleted: false,
                  contentCompleted: true,
                  completedBefore: true,
                  timerCompleted: true,
                });
              } else {
                this.setState({ pageNotCompleted: false });
              }
            },
            icon: '',
            title: i18n.translate('trainingScreens.viewAllTitle'),
            text: i18n.translate('trainingScreens.viewAll'),
            confirmButtonText: i18n.translate('general.close'),
          }}
        />
      </React.Fragment>
    );
  }
}

export default withRouter(TrainingContentView);
